// src/hooks/useMetricsData.js
import { useEffect, useState, useCallback } from 'react';
import { fetchAdvancedMetrics, fetchStockBundle, fetchHistoricalMetrics } from '../services/metricsService';

// ---- Flatten helpers (UI expects label-keyed maps) ----
function flattenIncome(income) {
  return {
    'Total Revenue': income?.revenue ?? 'N/A',
    'Gross Profit': income?.grossProfit ?? 'N/A',
    'Gross Profit Ratio': income?.grossProfitRatio ?? 'N/A',
    'EBITDA': income?.ebitda ?? 'N/A',
    'EBITDA Ratio': income?.ebitdaRatio ?? 'N/A',
    'Income Before Tax': income?.incomeBeforeTax ?? 'N/A',
    'Income Before Tax Ratio': income?.incomeBeforeTaxRatio ?? 'N/A',
    'Net Income': income?.netIncome ?? 'N/A',
    'Net Income Ratio': income?.netIncomeRatio ?? 'N/A',
    'Operating Income': income?.operatingIncome ?? 'N/A',
    'Operating Income Ratio': income?.operatingIncomeRatio ?? 'N/A',
    'Earnings Per Share': income?.epsDiluted ?? 'N/A',
  };
}

function flattenBalance(balance) {
  return {
    'Total Assets': balance?.totalAssets ?? 'N/A',
    'Total Current Assets': balance?.totalCurrentAssets ?? 'N/A',
    'Cash and Cash Equivalents': balance?.cashAndCashEquivalents ?? 'N/A',
    'Net Receivables': balance?.netReceivables ?? 'N/A',
    'Inventory': balance?.inventory ?? 'N/A',
    'Retained Earnings': balance?.retainedEarnings ?? 'N/A',
    'Total Equity': balance?.totalEquity ?? 'N/A',
    'Total Liabilities': balance?.totalLiabilities ?? 'N/A',
    'Total Current Liabilities': balance?.totalCurrentLiabilities ?? 'N/A',
    'Account Payable': balance?.accountPayable ?? 'N/A',
    'Net Debt': balance?.netDebt ?? 'N/A',
    'Common Stock': balance?.commonStock ?? 'N/A',
  };
}

function flattenCashflow(cashflow) {
  return {
    'Working Capital': cashflow?.workingCapital ?? 'N/A',
    'Change in Working Capital': cashflow?.changeInWorkingCapital ?? 'N/A',
    'Free Cash Flow': cashflow?.freeCashFlow ?? 'N/A',
    'Maintainable_FCF': cashflow?.maintainable_fcf ?? 'N/A',
    'Operating Cashflow': cashflow?.operatingCashFlow ?? 'N/A',
    'Net Change in Cash': cashflow?.netChangeInCash ?? 'N/A',
    'Cash at Beginning of Period': cashflow?.cashAtBeginningOfPeriod ?? 'N/A',
    'Cash at End of Period': cashflow?.cashAtEndOfPeriod ?? 'N/A',
    'Stock Based Compensation': cashflow?.stockBasedCompensation ?? 'N/A',
    'Common Stock Issuance': cashflow?.commonStockIssuance ?? 'N/A',
    'Common Stock Repurchased': cashflow?.commonStockRepurchased ?? 'N/A',
    'Dividends Paid': cashflow?.dividendsPaid ?? 'N/A',
  };
}

export function useMetricsData({ userEmail }) {
  // Basic metrics (label-keyed maps for the UI tiles)
  const [incomeMetrics, setIncomeMetrics] = useState(null);
  const [balanceMetrics, setBalanceMetrics] = useState(null);
  const [cashflowMetrics, setCashflowMetrics] = useState(null);

  // Aggregated store for pages that want the raw groups too
  const [metricsData, setMetricsData] = useState(null);

  // Multi-quarter cached data
  const [advancedByDate, setAdvancedByDate] = useState(null);
  const [basicByDate, setBasicByDate] = useState(null);
  const [availableQuarters, setAvailableQuarters] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedMetricQuarter, setSelectedMetricQuarter] = useState('');

  // Price + identity
  const [stockSymbol, setStockSymbol] = useState('');
  const [priceDetails, setPriceDetails] = useState(null);
  const [sharePrice, setSharePrice] = useState(null);
  const [sharePriceTimestamp, setSharePriceTimestamp] = useState('');

  // Visualisations
  const [historicalSeries, setHistoricalSeries] = useState([]);
  const [visualQuarters, setVisualQuarters] = useState(6);

  // UI state
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Derive displayed metrics from cached multi-quarter data for the selected quarter
  const applyQuarterSelection = useCallback((quarterLabel, dates, quarters, advData, basData, priceRaw) => {
    const idx = quarters.indexOf(quarterLabel);
    const dateKey = idx >= 0 ? dates[idx] : dates[0];

    // Advanced metrics for selected quarter
    const advForDate = advData?.[dateKey] || {};
    const efficiency = advForDate?.efficiency ?? {};
    const leverage = advForDate?.leverage ?? {};
    const profitability = advForDate?.profitability ?? {};
    const valuation = advForDate?.valuation ?? {};
    const earningsQuality = advForDate?.earningsQuality ?? {};

    // Basic metrics for selected quarter
    const basForDate = basData?.[dateKey] || {};
    const incomeRaw = basForDate?.income ?? {};
    const balanceRaw = basForDate?.balance ?? {};
    const cashflowRaw = basForDate?.cashflow ?? {};

    setIncomeMetrics(flattenIncome(incomeRaw));
    setBalanceMetrics(flattenBalance(balanceRaw));
    setCashflowMetrics(flattenCashflow(cashflowRaw));

    setMetricsData({
      income: incomeRaw,
      balance: balanceRaw,
      cashflow: cashflowRaw,
      price: priceRaw,
      efficiency,
      leverage,
      profitability,
      valuation,
      earningsQuality,
    });
  }, []);

  // When user changes the selected quarter, re-derive from cache
  useEffect(() => {
    if (!selectedMetricQuarter || !advancedByDate || !basicByDate) return;
    applyQuarterSelection(
      selectedMetricQuarter,
      availableDates,
      availableQuarters,
      advancedByDate,
      basicByDate,
      priceDetails
    );
  }, [selectedMetricQuarter, advancedByDate, basicByDate, availableDates, availableQuarters, priceDetails, applyQuarterSelection]);

  // IMPORTANT: keep same signature Controls uses: (company, quarter, exchangeCode)
  const refreshMetrics = async (company, quarter, exchangeCode) => {
    if (loading) return;
    setLoading(true);
    setErrorMessage('');

    try {
      const headers = {
        'X-User-Email': userEmail || '',
        Accept: 'application/json',
      };

      const companyName = company || 'Apple';
      const { symbol, data } = await fetchStockBundle(companyName, headers, exchangeCode);
      setStockSymbol(symbol);

      // Advanced groups (now returns multi-quarter: { quarters, dates, byDate })
      const advanced = await fetchAdvancedMetrics(symbol, headers);

      // Store multi-quarter advanced data
      const advQuarters = advanced?.quarters || [];
      const advDates = advanced?.dates || [];
      const advByDate = advanced?.byDate || {};
      setAdvancedByDate(advByDate);

      // Basic data - use the single-quarter response for now and build byDate from it
      // The existing endpoint returns latest only; build a compatible structure
      const incomeRaw = data?.income ?? {};
      const balanceRaw = data?.balance ?? {};
      const cashflowRaw = data?.cashflow ?? {};
      const priceRaw = data?.price ?? {};

      // Build basicByDate keyed by the same dates from advanced response
      // For now, we know the latest date matches the stock data response
      const basByDate = {};
      if (advDates.length > 0) {
        // The single stock endpoint only gives latest; place it at the first date
        basByDate[advDates[0]] = { income: incomeRaw, balance: balanceRaw, cashflow: cashflowRaw };
      }

      // Fetch multi-quarter basic data from new endpoint
      try {
        const basicMultiResp = await fetch(
          `https://metrics-8.onrender.com/api/stock/${encodeURIComponent(symbol)}/quarters?quarters=6`,
          { headers }
        );
        if (basicMultiResp.ok) {
          const basicMulti = await basicMultiResp.json();
          if (basicMulti?.byDate) {
            Object.assign(basByDate, basicMulti.byDate);
          }
        }
      } catch (e) {
        console.warn('[useMetricsData] multi-quarter basic fetch failed, using latest only:', e);
      }

      setBasicByDate(basByDate);

      // Set available quarters from the advanced response
      setAvailableQuarters(advQuarters);
      setAvailableDates(advDates);

      // Default to first (most recent) quarter
      const defaultQuarter = advQuarters[0] || '';
      setSelectedMetricQuarter(defaultQuarter);

      // Sidebar price bits
      setPriceDetails(priceRaw);
      setSharePrice(priceRaw?.price ?? null);

      const timestamp = new Date().toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      setSharePriceTimestamp(timestamp);

      // Apply the default quarter immediately
      applyQuarterSelection(defaultQuarter, advDates, advQuarters, advByDate, basByDate, priceRaw);
    } catch (err) {
      setErrorMessage(err?.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  
  // Load historical series for visualisations whenever symbol/quarters changes
  useEffect(() => {
    if (!stockSymbol) return;

    const headers = {
      'X-User-Email': userEmail || '',
      Accept: 'application/json',
    };

    let cancelled = false;

    (async () => {
      try {
        const history = await fetchHistoricalMetrics(stockSymbol, visualQuarters, headers);
        if (!cancelled) setHistoricalSeries(history?.series || []);
      } catch (e) {
        console.warn('[useMetricsData] historical fetch failed:', e);
        if (!cancelled) setHistoricalSeries([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stockSymbol, visualQuarters, userEmail]);

  return {
    // Basic page expects these maps
    incomeMetrics,
    balanceMetrics,
    cashflowMetrics,

    // Other pages can use this aggregated object
    metricsData,

    // Quarter selection
    availableQuarters,
    selectedMetricQuarter,
    setSelectedMetricQuarter,

    // Sidebar / identity
    stockSymbol,
    priceDetails,
    sharePrice,
    sharePriceTimestamp,

    // Visualisations
    historicalSeries,
    visualQuarters,
    setVisualQuarters,

    // Controls
    refreshMetrics,
    loading,
    errorMessage,
  };
}

